import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../UserContext';
import { BookOpen, Mail, Lock, AlertCircle, Eye, EyeOff, Sparkles, Heart, Star, ArrowLeft, Check, Shield } from 'lucide-react';
import { trackEvent } from '../utils/analyticsTracker';
import { usePageView } from '../utils/usePageView';
import { trackStartTrial, trackCompleteRegistration } from '../utils/metaConversions';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login, signup, sendPasswordReset } = useUser();
  const searchParams = new URLSearchParams(window.location.search);
  // /get-started's "Already have an account? Log in" link passes mode='login'
  // via router state — honor it so the visitor doesn't land on the signup tab.
  const initialMode = location.state?.mode || searchParams.get('mode');
  const fromTrial = location.state?.fromTrial;
  const [isSignUp, setIsSignUp] = useState(initialMode !== 'login');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const [resetError, setResetError] = useState('');

  // Whether the user just submitted the form and we're waiting to navigate.
  // This is STATE so that setting it triggers a re-render and the effect
  // below re-evaluates, even if user/authLoading were already settled.
  const [didSubmit, setDidSubmit] = useState(false);

  // No auto-redirect for logged-in users hitting /login.
  // Always show the form — require credentials to get to dashboard.
  // But the Firebase session stays alive so typing /dashboard directly
  // still works via ProtectedRoute (standard auth behavior).

  // After a successful login/signup, wait for auth to fully settle, then navigate.
  useEffect(() => {
    if (didSubmit && !authLoading && user) {
      setDidSubmit(false);
      navigate('/dashboard', { replace: true });
    }
  }, [didSubmit, authLoading, user, navigate]);

  usePageView('login');

  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) setIsPageLoading(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Floating background elements (desktop only, hidden on mobile via CSS)
  const floatingElementsRef = useRef(null);
  useEffect(() => {
    if (floatingElementsRef.current) { setFloatingElements(floatingElementsRef.current); return; }
    const elements = [];
    const icons = ['/assets/whfe.webp', '/assets/bfe.webp', '/assets/sfe.webp', '/assets/rfe.webp', '/assets/mfe.webp', '/assets/hfe.webp', '/assets/mgfe.webp', '/assets/pfe.webp'];
    const count = window.innerWidth < 640 ? 3 : 10;
    for (let i = 0; i < count; i++) {
      elements.push({
        id: i,
        icon: icons[i % icons.length],
        left: `${(i / count) * 100}%`,
        animationDuration: `${15 + (i * 3) % 10}s`,
        animationDelay: `${(i * 2) % 5}s`,
        size: `${30 + (i * 7) % 20}px`
      });
    }
    floatingElementsRef.current = elements;
    setFloatingElements(elements);
  }, []);

  // Pre-fill email — router state (from /get-started) wins over rememberedEmail.
  useEffect(() => {
    const prefilled = location.state?.prefilledEmail;
    if (prefilled) {
      setEmail(prefilled);
      return;
    }
    try {
      const rememberedEmail = localStorage.getItem('rememberedEmail');
      if (rememberedEmail) {
        setEmail(rememberedEmail);
        setRememberMe(true);
      }
    } catch { /* Safari private mode — localStorage unavailable */ }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let trialData = null;
    if (isSignUp) {
      try {
        const stored = sessionStorage.getItem('trialCompleted');
        if (stored) trialData = JSON.parse(stored);
      } catch {}
    }

    const result = isSignUp
      ? await signup(email, password, username, emailOptIn, trialData)
      : await login(email, password);

    if (result.success) {
      if (isSignUp) {
        trackEvent('signup', { email, ...(trialData && { fromTrial: true, trialPhase: trialData.phase, trialAgeGroup: trialData.ageGroup }) }, email);
        try { if (window.BrightReadingPixel?.startTrial) window.BrightReadingPixel.startTrial(); } catch {}
        // Meta Conversions API — CompleteRegistration + StartTrial
        const capiUserData = { em: email, fn: username };
        trackCompleteRegistration(capiUserData);
        trackStartTrial(capiUserData);
        sessionStorage.removeItem('trialCompleted');
      } else {
        trackEvent('login_success', { email }, email);
      }
      try {
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
      } catch { /* Safari private mode — localStorage unavailable */ }
      // Tell the useEffect we're ready to navigate.
      // It will wait for authLoading to be false before navigating.
      setDidSubmit(true);
      return;
    }

    console.error('Full error details:', result);
    setError(result.error || 'Unknown error occurred');
    setLoading(false);
  };

  const handleModeSwitch = () => {
    setIsSignUp(!isSignUp);
    setError('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetError('Please enter your email address.');
      return;
    }
    setResetStatus('sending');
    setResetError('');
    const result = await sendPasswordReset(resetEmail);
    if (result.success) {
      setResetStatus('sent');
    } else {
      setResetError(result.error);
      setResetStatus('error');
    }
  };

  return (
    <div className={`min-h-screen min-h-[100dvh] bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 md:from-purple-400 md:via-pink-400 md:to-orange-400 relative overflow-hidden`}>
      {/* Animated Background Elements - desktop only */}
      <div className="hidden md:block">
        {floatingElements.map((el) => (
          <div
            key={el.id}
            className="absolute animate-float opacity-20 pointer-events-none"
            style={{
              left: el.left,
              width: el.size,
              height: el.size,
              animationDuration: el.animationDuration,
              animationDelay: el.animationDelay,
              top: '-50px',
              willChange: 'transform',
              contain: 'strict'
            }}
          >
            <img src={el.icon} alt="" loading="lazy" className="w-full h-full object-contain" />
          </div>
        ))}

        {/* Decorative Circles - desktop only */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
      </div>

      {/* Back to Landing */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-purple-600 md:text-white/80 hover:text-purple-800 md:hover:text-white bg-white/80 md:bg-white/10 hover:bg-white md:hover:bg-white/20 px-3 py-2 rounded-full transition text-sm font-bold"
        >
          <ArrowLeft size={16} />
          Home
        </button>
      </div>

      {/* Main Content */}
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center px-4 py-16 sm:py-12 relative z-10">
        <div className="max-w-6xl w-full grid md:grid-cols-2 gap-4 sm:gap-8 items-center">
          
          {/* Left Side - Welcome Message */}
          <div className="text-white space-y-6 hidden md:block">
            <div className="inline-flex items-center gap-3 bg-white/20 px-6 py-3 rounded-full border-2 border-white/30">
              <Sparkles size={24} />
              <span className="font-bold">{isSignUp ? (fromTrial ? 'Continue Your Free Trial' : 'Start Your Reading Journey') : 'Continue Your Reading Journey'}</span>
            </div>
            
            <h1 className="text-6xl font-black leading-tight">
              {isSignUp ? (
                fromTrial ? (
                  <>
                    Enjoyed that?
                    <br />
                    <span className="text-yellow-200">1,000+ more activities are waiting.</span>
                  </>
                ) : (
                  <>
                    Your child learns to read best from you.
                    <br />
                    <span className="text-yellow-200">Not from a screen. Start here.</span>
                  </>
                )
              ) : (
                <>
                  Welcome Back,
                  <br />
                  <span className="text-yellow-200">Super Parent!</span>
                </>
              )}
            </h1>
            
            <p className="text-xl text-white/90 font-semibold">
              {isSignUp
                ? (fromTrial
                    ? 'Create your free account and keep going. No credit card needed.'
                    : "You don't need a teaching degree. You need 10 minutes and the right words.")
                : 'Your little reader is waiting to continue their adventure with you!'
              }
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3 pt-4">
              {['No ads', 'No data sold', 'Zero child screen time'].map((label) => (
                <div key={label} className="bg-white/20 px-4 py-2 rounded-full border border-white/30 flex items-center gap-2">
                  <Shield size={16} className="text-green-300" />
                  <span className="text-sm font-bold">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Login Form Card */}
          <div className="relative">
            {/* Decorative Badge - desktop only */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-6 py-2 rounded-full font-black text-sm shadow-lg z-20 border-4 border-white hidden md:block">
              {isSignUp ? (fromTrial ? "YOU'RE ALMOST THERE" : 'GET STARTED FREE') : 'WELCOME BACK'}
            </div>

            <div className={`bg-white rounded-2xl sm:rounded-3xl shadow-md md:shadow-2xl p-5 sm:p-8 md:p-10 relative overflow-hidden`}>
              {/* Subtle Pattern Overlay */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle, #9333ea 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>
              </div>

              <div className="relative z-10">
                {/* Logo & Title */}
                <div className="text-center mb-4 sm:mb-8">
                  <div className="mx-auto mb-3 sm:mb-4">
                    <img src="/assets/brlogo.webp" alt="BrightReading Logo" className="h-14 sm:h-20 w-auto object-contain mx-auto" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1 sm:mb-2">
                    {isSignUp ? 'Join BrightReading' : 'Welcome Back'}
                  </h2>
                  <p className="text-gray-600 font-semibold">
{isSignUp ? (fromTrial ? 'Create your free account and keep going.' : "Your child's reading journey starts with you.") : 'Continue your learning journey'}                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3 animate-shake">
                    <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                      <AlertCircle className="text-red-600" size={20} />
                    </div>
                    <p className="text-red-700 font-semibold text-sm">{error}</p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-5">
                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                      <Mail size={16} className="text-purple-500" />
                      Email Address
                    </label>
                    <div className="relative group">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 sm:py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-base sm:text-lg font-semibold group-hover:border-purple-400"
                        placeholder="your@email.com"
                        required
                        autoComplete="email"
                        inputMode="email"
                      />
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl opacity-0 group-focus-within:opacity-100 transition">
                        ✉️
                      </div>
                    </div>
                  </div>

                  {/* Username Field - Only show on signup */}
                  {isSignUp && (
                    <div>
                      <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                        <BookOpen size={16} className="text-purple-500" />
                        Username
                      </label>
                      <div className="relative group">
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full px-4 py-3 sm:py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-base sm:text-lg font-semibold group-hover:border-purple-400"
                          placeholder="coolparent123"
                          required
                          autoComplete="username"
                        />
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-2xl opacity-0 group-focus-within:opacity-100 transition">
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                      <Lock size={16} className="text-purple-500" />
                      Password
                    </label>
                    <div className="relative group">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 sm:py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-base sm:text-lg font-semibold group-hover:border-purple-400 pr-14"
                        placeholder="••••••••"
                        required
                        autoComplete={isSignUp ? "new-password" : "current-password"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-600 transition p-2.5 rounded-lg hover:bg-purple-50"
                      >
                        {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                      </button>
                    </div>
                    {!isSignUp && (
                      <div className="text-right mt-2">
                        <button
                          type="button"
                          onClick={() => { setResetEmail(email); setResetStatus(''); setResetError(''); setShowForgotPassword(true); }}
                          className="text-purple-600 hover:text-purple-700 text-sm font-bold hover:underline transition"
                        >
                          Forgot your password?
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Signup: Email opt-in / Login: Remember Me */}
                  {isSignUp ? (
                    <div className="flex items-start gap-3 bg-green-50 rounded-xl p-3 border-2 border-green-100">
                      <input
                        type="checkbox"
                        id="emailOptIn"
                        checked={emailOptIn}
                        onChange={(e) => setEmailOptIn(e.target.checked)}
                        className="w-5 h-5 text-green-600 border-green-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer mt-0.5"
                      />
                      <label htmlFor="emailOptIn" className="text-sm font-bold text-gray-700 cursor-pointer select-none leading-snug">
                        Mind if we check in after your first activity? We'd love to hear how it went.
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-3 border-2 border-purple-100">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-purple-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer"
                      />
                      <label htmlFor="rememberMe" className="text-sm font-bold text-gray-700 cursor-pointer select-none flex items-center gap-2">
                        Remember me
                        <span className="text-lg">🧠</span>
                      </label>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 sm:py-5 rounded-2xl font-black text-base sm:text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-2 border-4 border-white shadow-lg hover:border-purple-200"
                  >
                    {loading ? (
                      <>
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        Please wait...
                      </>
                    ) : (
                      <>
                        {isSignUp ? '🚀 Create My Account' : '✨ Sign Me In'}
                      </>
                    )}
                  </button>
                </form>

                {/* Toggle Sign In/Up */}
                <div className="mt-5 sm:mt-8 text-center">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t-2 border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500 font-bold">OR</span>
                    </div>
                  </div>

                  <button
                    onClick={handleModeSwitch}
                    className="mt-4 sm:mt-6 text-purple-600 hover:text-purple-700 font-black text-base sm:text-lg hover:underline transition transform hover:scale-105 active:scale-95"
                  >
                    {isSignUp ? '👋 Already have an account? Sign in!' : "🎉 Don't have an account? Sign up!"}
                  </button>
                </div>

                {/* Back to Home */}
                <div className="mt-4 sm:mt-8 pt-4 sm:pt-6 border-t-2 border-gray-100 text-center">
                  <button
                    onClick={() => {
                      navigate('/');
                      window.scrollTo(0, 0);
                    }}
                    className="text-gray-600 hover:text-gray-900 font-bold text-sm inline-flex items-center gap-2 hover:gap-3 transition-all transform hover:scale-105 active:scale-95"
                  >
                    ← Back to home
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Welcome Message - removed, form is front and center */}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-5 sm:p-8 relative overflow-hidden my-auto">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, #9333ea 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} />
            </div>
            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-purple-200">
                  <Mail size={32} className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-1">Reset Password</h2>
                <p className="text-gray-600 font-semibold text-sm">We'll send you a link to create a new password</p>
              </div>

              {resetStatus === 'sent' ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-green-200">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-2">Check Your Email!</h3>
                  <p className="text-gray-600 font-semibold text-sm mb-6">
                    We sent a password reset link to <span className="font-black text-purple-600">{resetEmail}</span>
                  </p>
                  <button
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-xl transition transform hover:scale-[1.03] active:scale-95 border-4 border-white shadow-lg"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-sm font-black text-gray-700 mb-2 flex items-center gap-2">
                      <Mail size={16} className="text-purple-500" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => { setResetEmail(e.target.value); setResetError(''); }}
                      className="w-full px-4 py-4 border-3 border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 focus:border-purple-500 transition text-lg font-semibold"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  {resetError && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-start gap-3">
                      <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                        <AlertCircle className="text-red-600" size={20} />
                      </div>
                      <p className="text-red-700 font-semibold text-sm">{resetError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={resetStatus === 'sending'}
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-4 rounded-2xl font-black text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.03] active:scale-95 flex items-center justify-center gap-2 border-4 border-white shadow-lg"
                  >
                    {resetStatus === 'sending' ? (
                      <>
                        <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-4 rounded-2xl font-black text-lg transition"
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
        @keyframes float {
          0% {
            transform: translateY(-50px) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-10px);
          }
          75% {
            transform: translateX(10px);
          }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;